import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Scale, Heart, ArrowRight, MapPin, X, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { fmtUSD, FLAGS } from "@/lib/utils";
import PropertyCard from "@/components/PropertyCard";

function SortControl({ sort, setSort, testId }) {
  return (
    <div className="flex items-center gap-2">
      <select
        data-testid={testId}
        value={sort}
        onChange={(e) => setSort(e.target.value)}
        className="px-4 py-2 rounded-full bg-white border border-neutral-200 text-xs font-medium focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none"
      >
        <option value="newest">Newest</option>
        <option value="price_low">Price: Low to High</option>
        <option value="price_high">Price: High to Low</option>
      </select>
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        onClick={() => {
          if (sort === "price_high") setSort("price_low");
          else setSort("price_high");
        }}
        className={`w-9 h-9 rounded-full flex items-center justify-center border transition ${sort === "price_high" || sort === "price_low" ? "bg-propera text-white border-propera hover:bg-propera-hover" : "bg-white border-neutral-200 text-neutral-500 hover:border-neutral-300"}`}
        aria-label="Toggle sort direction"
        title={sort === "price_high" ? "Switch to Low → High" : "Switch to High → Low"}
      >
        {sort === "price_high" ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
      </button>
    </div>
  );
}

export default function Portfolio() {
  const { refreshUser } = useAuth();
  const [tab, setTab] = useState("watchlist");
  const [investments, setInvestments] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [watchSort, setWatchSort] = useState("newest");
  const [compareSort, setCompareSort] = useState("newest");

  const sortedWatch = useMemo(() => {
    const list = [...watchlist];
    if (watchSort === "price_low") list.sort((a, b) => (a.unit?.price || 0) - (b.unit?.price || 0));
    else if (watchSort === "price_high") list.sort((a, b) => (b.unit?.price || 0) - (a.unit?.price || 0));
    return list;
  }, [watchlist, watchSort]);

  const sortedComparisons = useMemo(() => {
    const list = [...comparisons];
    if (compareSort === "price_low") list.sort((a, b) => (a.starting_price || 0) - (b.starting_price || 0));
    else if (compareSort === "price_high") list.sort((a, b) => (b.starting_price || 0) - (a.starting_price || 0));
    return list;
  }, [comparisons, compareSort]);

  async function loadAll() {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/portfolio"),
        api.get("/comparisons"),
        api.get("/watchlist"),
      ]);
      setInvestments(a.data);
      setComparisons(b.data);
      setWatchlist(c.data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadAll(); }, []);

  async function removeWatch(unitId) {
    try { await api.post(`/watchlist/toggle/${unitId}`); await loadAll(); await refreshUser(); } catch { /* ignore */ }
  }
  async function removeComparison(devId) {
    try { await api.post(`/comparisons/toggle/${devId}`); await loadAll(); await refreshUser(); } catch { /* ignore */ }
  }

  return (
    <div className="max-w-7xl mx-auto">
      <p className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Portfolio</p>
      <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mt-2">Saved & investments</h1>
      <p className="text-sm sm:text-base text-neutral-500 mt-2">Track watchlisted units, side-by-side comparisons, and allocated investments.</p>

      <div className="mt-6 sm:mt-8 -mx-6 lg:mx-0 px-6 lg:px-0 overflow-x-auto no-scrollbar border-b border-neutral-200">
        <div className="flex gap-1 min-w-max">
          <Tab active={tab === "watchlist"} onClick={() => setTab("watchlist")} testId="tab-watchlist">
            <Heart size={14} className="inline mr-1.5 -mt-0.5" /> Watchlist ({watchlist.length})
          </Tab>
          <Tab active={tab === "comparisons"} onClick={() => setTab("comparisons")} testId="tab-comparisons">
            <Scale size={14} className="inline mr-1.5 -mt-0.5" /> Compare ({comparisons.length}/2)
          </Tab>
          <Tab active={tab === "investments"} onClick={() => setTab("investments")} testId="tab-investments">
            <Briefcase size={14} className="inline mr-1.5 -mt-0.5" /> Investments ({investments.length})
          </Tab>
        </div>
      </div>

      {/* WATCHLIST */}
      {tab === "watchlist" && (
        loading ? null : watchlist.length === 0 ? (
          <Empty icon={Heart} title="No saved units yet" body="Tap the heart on any available unit to save it here. We'll keep track of price and status so you don't have to." />
        ) : (
          <>
            <div className="mt-6 flex items-center justify-end">
              <SortControl sort={watchSort} setSort={setWatchSort} testId="watchlist-sort" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
              {sortedWatch.map(({ unit, development }) => (
              <div key={unit.id} className="soft-card overflow-hidden relative">
                {development?.cover_image && <img src={development.cover_image} alt="" className="w-full h-36 object-cover" />}
                <button data-testid={`unwatch-${unit.id}`} onClick={() => removeWatch(unit.id)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/95 text-neutral-600 hover:bg-white flex items-center justify-center" title="Remove">
                  <X size={14} />
                </button>
                <div className="p-5">
                  <p className="font-semibold text-neutral-900">{development?.name}</p>
                  <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {development?.city}, {development?.country}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-propera-light text-propera">{unit.label} · {unit.bedrooms}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full ${unit.status === "available" ? "bg-green-50 text-emerald-600" : "bg-neutral-100 text-neutral-500"}`}>{unit.status}</span>
                  </div>
                  <p className="text-xl font-bold text-neutral-900 tabular mt-3">{fmtUSD(unit.price)}</p>
                  <Link to={`/development/${development?.id}`} data-testid={`view-dev-${development?.id}`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-propera hover:underline">View development <ArrowRight size={11} /></Link>
                </div>
              </div>
              ))}
            </div>
          </>
        )
      )}

      {/* COMPARE */}
      {tab === "comparisons" && (
        comparisons.length === 0 ? (
          <Empty icon={Scale} title="No developments to compare yet" body="Use the scale icon on any property card to add up to 2 developments side-by-side. Great for weighing trade-offs before you decide." />
        ) : comparisons.length === 1 ? (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <PropertyCard dev={comparisons[0]} />
            <Link to="/discover" data-testid="compare-add-second" className="soft-card flex flex-col items-center justify-center text-center p-12 border-dashed">
              <div className="w-14 h-14 rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-3"><Scale size={22} /></div>
              <p className="font-semibold text-neutral-900">Add a second development</p>
              <p className="text-xs text-neutral-500 mt-1 max-w-xs">Browse Discover and tap the scale icon on another property to compare.</p>
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-6 flex items-center justify-end">
              <SortControl sort={compareSort} setSort={setCompareSort} testId="compare-sort" />
            </div>
            <CompareView devs={sortedComparisons} onRemove={removeComparison} />
          </>
        )
      )}

      {/* INVESTMENTS */}
      {tab === "investments" && (
        loading ? null : investments.length === 0 ? (
          <Empty icon={Briefcase} title="No investments yet" body="Once a developer allocates a unit to you, your investment will appear here with payment progress and updates." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
            {investments.map((inv) => (
              <div key={inv.unit?.id} className="soft-card overflow-hidden">
                <img src={inv.development?.cover_image} alt="" className="w-full h-44 object-cover" />
                <div className="p-5">
                  <p className="font-semibold text-neutral-900">{inv.development?.name}</p>
                  <p className="text-xs text-neutral-500 flex items-center gap-1 mt-1"><MapPin size={12} /> {inv.development?.city}, {inv.development?.country}</p>
                  <div className="flex items-end justify-between mt-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-neutral-400">Investment value</p>
                      <p className="text-xl font-bold tabular">{fmtUSD(inv.unit?.price)}</p>
                    </div>
                    <span className="text-xs font-semibold text-propera">{inv.unit?.label}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function CompareView({ devs, onRemove }) {
  const rows = [
    { label: "Starting Price", key: (d) => fmtUSD(d.starting_price) },
    { label: "Total Units", key: (d) => d.total_units ?? "—" },
    { label: "Available", key: (d) => d.available_units ?? "—" },
    { label: "Interested investors", key: (d) => d.interest_count ?? 0 },
    { label: "Country", key: (d) => `${FLAGS[d.country] || ""} ${d.country}` },
    { label: "City", key: (d) => d.city },
    { label: "Completion", key: (d) => d.completion_date || "—" },
    { label: "Tags", key: (d) => (d.tags || []).join(", ") || "—" },
    { label: "Amenities", key: (d) => (d.amenities || []).length + " included" },
    { label: "Payment Plan", key: (d) => d.payment_plan ? `${d.payment_plan.booking}% / ${d.payment_plan.construction}% / ${d.payment_plan.handover}%` : "—" },
  ];
  return (
    <div className="mt-8" data-testid="compare-view">
      {/* Mobile/tablet: horizontal-scroll side-by-side cards (keeps the compare feel on small screens) */}
      <div className="md:hidden -mx-6">
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-6 pb-4 no-scrollbar">
          {devs.map((d) => (
            <div key={d.id} className="soft-card overflow-hidden flex-shrink-0 w-[82vw] max-w-[340px] snap-start">
              <div className="relative">
                <img src={d.cover_image} alt="" className="w-full h-36 object-cover" />
                <button data-testid={`compare-remove-${d.id}`} onClick={() => onRemove(d.id)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 hover:bg-white flex items-center justify-center text-neutral-600" title="Remove">
                  <X size={14} />
                </button>
              </div>
              <div className="p-4">
                <p className="font-bold text-neutral-900 truncate">{d.name}</p>
                <Link to={`/development/${d.id}`} className="text-xs text-propera hover:underline">View details →</Link>
                <dl className="mt-3 divide-y divide-neutral-100 text-sm">
                  {rows.map((r) => (
                    <div key={r.label} className="flex justify-between gap-3 py-2">
                      <dt className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold flex-shrink-0">{r.label}</dt>
                      <dd className="text-neutral-900 font-medium text-right break-words">{r.key(d)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          ))}
        </div>
        <p className="px-6 mt-1 text-[11px] text-neutral-400 font-medium">← Swipe to compare →</p>
      </div>
      {/* Desktop: side-by-side table */}
      <div className="hidden md:block soft-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left px-6 py-4 font-semibold text-xs uppercase tracking-wider text-neutral-500 w-40">&nbsp;</th>
              {devs.map((d) => (
                <th key={d.id} className="px-4 py-4 text-left min-w-[260px]">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <img src={d.cover_image} alt="" className="w-full h-32 object-cover rounded-xl mb-3" />
                      <p className="font-bold text-neutral-900 text-base">{d.name}</p>
                      <Link to={`/development/${d.id}`} className="text-xs text-propera hover:underline">View details →</Link>
                    </div>
                    <button data-testid={`compare-remove-desktop-${d.id}`} onClick={() => onRemove(d.id)} className="w-8 h-8 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center text-neutral-500" title="Remove from comparison">
                      <X size={14} />
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t border-neutral-100">
                <td className="px-6 py-3 text-xs uppercase tracking-wider text-neutral-500 font-semibold">{r.label}</td>
                {devs.map((d) => (
                  <td key={d.id} className="px-4 py-3 text-neutral-900 font-medium">{r.key(d)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tab({ active, onClick, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`px-5 py-3 text-sm font-semibold transition border-b-2 whitespace-nowrap ${active ? "text-propera border-propera" : "text-neutral-500 border-transparent hover:text-neutral-900"}`}>
      {children}
    </button>
  );
}

function Empty({ icon: Icon, title, body }) {
  return (
    <div className="rounded-3xl border border-dashed border-neutral-200 bg-white p-12 text-center mt-8">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-4">
        <Icon size={22} />
      </div>
      <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
      <p className="text-sm text-neutral-500 max-w-md mx-auto mt-1">{body}</p>
      <Link to="/discover" data-testid="empty-discover-cta" className="inline-flex items-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2.5 font-semibold text-sm mt-5">
        Discover Developments <ArrowRight size={14} />
      </Link>
    </div>
  );
}
